import clsx from "clsx";
import { Fragment, useMemo } from "react";

import { countryCodeOptions } from "@/features/auth/components/PhoneField";
import { withConfirmDialog } from "@/features/common/contexts/useConfirmDialogContext";
import { jobTypes } from "@/features/job/const/jobTypes";
import { ModelIG } from "@/features/model/components/profile/ModelIG";
import { ModelWithPrivateInfoFragment } from "@/generated/graphql-hooks";
import HideIcon from "@/icons/hide.svg";

export const PersonalInfo = withConfirmDialog(
  ({ model }: { model: ModelWithPrivateInfoFragment }) => {
    const personalInfoItems = useMemo(() => {
      return [
        {
          name: "⾝⾼體重",
          value: `${model.height || "-"}cm / ${model.weight || "-"}kg`,
        },
        ...(model.chest || model.cupSize || model.waist || model.hips
          ? [
              {
                name: "三圍",
                value: `${model.chest || "-"}${model.cupSize || ""} / ${
                  model.waist || "-"
                } / ${model.hips || "-"}`,
              },
            ]
          : []),
        {
          name: "鞋碼",
          value: model.shoeSize || "-",
        },
        {
          name: "擅長類型",
          value:
            model.prefJobType
              ?.map((jobType) => jobTypes[jobType])
              ?.join("、") || "-",
        },
        {
          name: "Instagram",
          value: model.instagram ? (
            <ModelIG instagram={model.instagram} />
          ) : (
            "-"
          ),
        },
        {
          name: "YouTube",
          value: model.youtube ? (
            <a
              className="text-primary"
              href={`https://youtube.com/@${model.youtube.trim()}/`}
              target="_blank"
              rel="noreferrer"
            >{`youtube.com/@${model.youtube.trim()}`}</a>
          ) : (
            "-"
          ),
        },
        {
          name: "小紅書號",
          value: model.littleRedBook ?? "-",
        },
        {
          name: "工作電話",
          ...(!model.canAccessPrivate && { value: <HideIcon /> }),
          ...(!!model.privateInfo && {
            value: model.privateInfo.phone?.length
              ? model.privateInfo.phone.replace(countryCodeOptions[0].label, "")
              : "-",
          }),
        },
        {
          name: "工作電郵",
          ...(!model.canAccessPrivate && { value: <HideIcon /> }),
          ...(!!model.privateInfo && {
            value: model.privateInfo.email?.length ? (
              <a
                href={`mailto:${model.privateInfo.email}`}
                className="text-primary"
              >
                {model.privateInfo.email}
              </a>
            ) : (
              "-"
            ),
          }),
        },
        {
          name: "額外資訊",
          ...(!model.canAccessPrivate && { value: <HideIcon /> }),
          ...(!!model.privateInfo && {
            value: model.privateInfo.extraInfo?.length
              ? model.privateInfo.extraInfo
              : "-",
          }),
        },
      ];
    }, [model]);

    return (
      <div className="bg-secondary grid grid-cols-3 gap-x-0.5 rounded-md border border-white text-base xl:grid-cols-6">
        {personalInfoItems.map(({ name, value }, index) => (
          <Fragment key={index}>
            <div
              className={clsx(
                !(index % 2) ? "bg-white" : "",
                "p-2 font-light text-gray-700",
              )}
            >
              {name}
            </div>
            <div
              className={clsx(
                !(index % 2) ? "bg-white" : "",
                "col-span-2 truncate p-2 font-normal xl:col-span-5",
              )}
            >
              {value}
            </div>
          </Fragment>
        ))}
      </div>
    );
  },
);
